import React from 'react';
import { Box, ButtonBase, alpha } from '@mui/material';
import type { Theme } from '@mui/material/styles';

const SegmentDot: React.FC<{ color: string }> = ({ color }) => (
  <Box component="span" aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flex: 'none' }} />
);

/** One control language for the filter row: 32px tall, 8px corners, a blue tint when selected, no weight change. */
export const CONTROL_SX = {
  height: 32,
  px: 1.5,
  borderRadius: '8px',
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1,
  textTransform: 'none',
  color: 'text.primary',
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  transition: (theme: Theme) => theme.transitions.create(['background-color', 'border-color', 'color'], { duration: 120 }),
  '&:hover': { bgcolor: 'action.hover' },
} as const;

export const SELECTED_SX = {
  bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
  borderColor: 'primary.main',
  color: 'primary.dark',
  '&:hover': { bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.12) },
} as const;

interface FilterChipProps {
  label: string;
  count: number;
  color?: string;
  active: boolean;
  onClick: () => void;
}

/** A segment filter: an optional colour dot, the label, and the count in a quieter tone. */
export const FilterChip: React.FC<FilterChipProps> = ({ label, count, color, active, onClick }) => (
  <ButtonBase
    aria-pressed={active}
    aria-label={`${label} ${count}`}
    onClick={onClick}
    sx={{
      ...CONTROL_SX,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      ...(active ? SELECTED_SX : null),
      '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
    }}
  >
    {color ? <SegmentDot color={color} /> : null}
    <span>{label}</span>
    <Box component="span" sx={{ color: active ? 'primary.dark' : 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
      {count}
    </Box>
  </ButtonBase>
);
